import { isEqual } from 'lodash';
import Chart from 'common/chartjs/register';

import { defaultFontFamily } from 'common/utils/fonts';

function normalizeTooltipPadding(padding) {
  if (typeof padding === 'number') {
    return `${padding}px ${padding}px`;
  }

  if (padding && typeof padding === 'object') {
    const x = padding.x ?? padding.left ?? 0;
    const y = padding.y ?? padding.top ?? 0;
    return `${y}px ${x}px`;
  }

  return '0px';
}

export default function customizeChartJSTooltip() {
  Chart.defaults.font.family = defaultFontFamily();
  Chart.defaults.font.size = 14;
  Chart.defaults.color = '#77777C';
  Chart.defaults.elements.line.borderCapStyle = 'round';
  Chart.defaults.elements.point.hitRadius = 10;
  Chart.defaults.elements.point.hoverRadius = 10;
  Chart.defaults.plugins.legend.position = 'bottom';
  Chart.defaults.plugins.legend.labels.boxWidth = 16;
  Chart.defaults.plugins.legend.labels.padding = 20;
  Chart.defaults.transitions.active.animation.duration = 400;
  Chart.defaults.plugins.tooltip.enabled = false;
  Chart.defaults.plugins.tooltip.intersect = true;
  Chart.defaults.plugins.tooltip.mode = 'nearest';
  Chart.defaults.plugins.tooltip.position = 'nearest';
  Chart.defaults.plugins.tooltip.padding = 10;
  Chart.defaults.layout = {
    padding: {
      top: 10,
      right: 15,
      bottom: 10,
      left: 15,
    },
  };

  Chart.defaults.onHover = function pointOnHover(event, activeElements, chart) {
    chart.canvas.style.cursor = activeElements.length ? 'pointer' : 'default';
  };

  Chart.defaults.plugins.legend.onHover = function legendOnHover(event) {
    event?.native?.target?.style?.setProperty('cursor', 'pointer');
  };

  Chart.defaults.plugins.legend.onLeave = function legendOnLeave(event) {
    event?.native?.target?.style?.setProperty('cursor', 'default');
  };

  // http://www.chartjs.org/docs/latest/configuration/tooltip.html#external-custom-tooltips
  Chart.defaults.plugins.tooltip.external = function customTooltip(context) {
    const { chart, tooltip } = context;
    const { canvas } = chart;
    const canvasContainer = canvas.parentElement;
    let tooltipEl = canvasContainer.querySelector('.chartjs-tooltip');

    // Create element on first render
    if (!tooltipEl) {
      tooltipEl = document.createElement('div');
      tooltipEl.className = 'chartjs-tooltip';
      tooltipEl.innerHTML = '<div style="max-width:75vw"><table></table></div>';
      canvasContainer.appendChild(tooltipEl);
    }

    // Hide if no tooltip
    if (tooltip.opacity === 0) {
      tooltipEl.style.visibility = 'hidden';
      tooltipEl.style.opacity = 0;
      return;
    }

    // Set caret Position
    tooltipEl.classList.remove('above', 'below', 'no-transform');
    if (tooltip.yAlign) {
      tooltipEl.classList.add(tooltip.yAlign);
    } else {
      tooltipEl.classList.add('no-transform');
    }

    function getBody(bodyItem) {
      return bodyItem.lines;
    }

    // Set Text
    if (tooltip.body) {
      const titleLines = tooltip.title || [];
      const beforeBodyLines = tooltip.beforeBody || [];
      const bodyLines = tooltip.body.map(getBody);
      const afterBodyLines = tooltip.afterBody || [];
      const footerLines = tooltip.footer || [];

      let innerHtml = '<thead>';

      titleLines.forEach(title => {
        innerHtml += `<tr><th ${
          chart.options.doNotTranslateTooltipTitle ? 'data-notranslate' : ''
        }>${title}</th></tr>`;
      });
      innerHtml += '</thead><tbody>';

      beforeBodyLines.forEach(bodyLine => {
        innerHtml += `<tr><td><span class="chartjs-tooltip-bodyline">${bodyLine}</span></td></tr>`;
      });

      bodyLines.forEach((body, i) => {
        const colors = tooltip.labelColors[i];
        let style = `background:${colors.backgroundColor}`;
        style += `; border-color:${colors.borderColor}`;
        style += '; border-width: 2px';

        const { datasetIndex } = tooltip.dataPoints[i] || tooltip.dataPoints[0];
        const isDashed = isEqual(chart.data.datasets[datasetIndex].borderDash, [5, 5]);

        const borderStyle = isDashed ? 'dashed' : 'solid';
        style += `; border-style: ${borderStyle}`;

        const span = `<span class="chartjs-tooltip-key" style="${style}"></span>`;
        innerHtml += `<tr><td>${span}<span class="chartjs-tooltip-value" ${
          chart.options.doNotTranslateTooltipValue ? 'data-notranslate' : ''
        }>${body}</span></td></tr>`;
      });

      afterBodyLines.forEach(bodyLine => {
        innerHtml += `<tr><td><span class="chartjs-tooltip-bodyline">${bodyLine}</span></td></tr>`;
      });

      innerHtml += '</tbody><tfoot>';
      footerLines.forEach(footer => {
        innerHtml += `<tr><th ${
          chart.options.doNotTranslateTooltipTitle ? 'data-notranslate' : ''
        }>${footer}</th></tr>`;
      });
      innerHtml += '</tfoot>';

      const tableRoot = tooltipEl.querySelector('table');
      tableRoot.innerHTML = innerHtml;
    }

    // Display, position, and set styles for font
    tooltipEl.style.visibility = 'visible';
    tooltipEl.style.opacity = 1;

    // where the tooltip should go
    const sumOfCanvasTooltipModelLeft = canvas.offsetLeft + tooltip.caretX;
    const tooltipElemOffsetWidth = tooltipEl.offsetWidth * 0.5;
    let tooltipLeft = sumOfCanvasTooltipModelLeft - tooltipElemOffsetWidth;

    const sumOfCanvasTooltipModelTop = canvas.offsetTop + tooltip.caretY;
    const tooltipElemOffsetHeight = tooltipEl.offsetHeight - 15;

    const tooltipTop = sumOfCanvasTooltipModelTop - tooltipElemOffsetHeight;

    const tooltipLeftAbsolute = canvasContainer.getBoundingClientRect().left + tooltipLeft;
    if (tooltipLeftAbsolute < 10) {
      tooltipLeft = 10 - canvasContainer.getBoundingClientRect().left;
    }

    const tooltipRightAbsolute =
      canvasContainer.getBoundingClientRect().left + tooltipLeft + tooltipEl.offsetWidth;

    if (tooltipRightAbsolute > document.documentElement.clientWidth) {
      const offset = canvasContainer.getBoundingClientRect().left + tooltipEl.offsetWidth + 10;
      tooltipLeft = document.documentElement.clientWidth - offset;
    }

    tooltipEl.style.left = `${tooltipLeft}px`;
    tooltipEl.style.top = `${tooltipTop}px`;
    tooltipEl.style.padding = normalizeTooltipPadding(tooltip.options.padding);
  };
}
